// Privacy mode — hide the company's dollar figures while screen-sharing.
//
// One boolean, toggled by the 👁/🙈 button in the MetricsBar and persisted in
// localStorage (best-effort: every read/write is try/catch-wrapped, so a
// blocked or private browser just falls back to session-only state). All
// subscribed components stay in sync through a tiny module-level listener set.
//
// Scope guard: only DISPLAY dollars go through maskMoney — revenue metrics,
// lifetime values, ad spend/ROAS-table dollars. The 💳 credit indicator and
// the draft text itself are OPERATIONAL (they decide what credit gets filed
// on send) and must NEVER be masked; they never call maskMoney. Counts and
// percentages stay visible either way.

import { useEffect, useState } from "react";

const STORAGE_KEY = "fresh-crm:privacy-mode";

function readStored(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

let current = readStored();
const listeners = new Set<(on: boolean) => void>();

function setPrivacy(on: boolean): void {
  current = on;
  try {
    localStorage.setItem(STORAGE_KEY, on ? "1" : "0");
  } catch {
    // Storage unavailable — the toggle still works for this session.
  }
  for (const l of listeners) l(on);
}

/**
 * [privacyOn, setPrivacyOn] — shared across every subscribed component, so
 * the MetricsBar toggle instantly masks the Home board, queue card, and Ads
 * tables too.
 */
export function usePrivacy(): [boolean, (on: boolean) => void] {
  const [on, setOn] = useState(current);
  useEffect(() => {
    const l = (v: boolean) => setOn(v);
    listeners.add(l);
    setOn(current); // re-sync in case it changed between render and effect
    return () => {
      listeners.delete(l);
    };
  }, []);
  return [on, setPrivacy];
}

/**
 * "$1,234.56" → "$ ••••" when privacy mode is on. Placeholders without a
 * dollar sign ("—", "…") and plain counts pass through untouched, so this is
 * safe to wrap around any already-formatted money() output.
 */
export function maskMoney(formatted: string, on: boolean): string {
  if (!on || !formatted.includes("$")) return formatted;
  return "$ ••••";
}
